import React from 'react';
import "./HighlightedText.css";

interface HighlightedTextProps {
  indices: [number, number][];  // Array of [startIndex, endIndex] tuples
  text: string;
  onClick?: () => void;
  style?: React.CSSProperties;
}

/**
 * HighlightedText component that renders text with highlighted sections based on provided indices
 * @param {HighlightedTextProps} props - Component props containing text and highlight indices
 * @returns {JSX.Element} Rendered component with highlighted text
 */
const HighlightedText: React.FC<HighlightedTextProps> = ({ indices, text, onClick, style }): JSX.Element => {
  const highlightText = (): JSX.Element[] => {
    const result: JSX.Element[] = [];
    let lastIndex = 0;

    indices.forEach(([startIndex, endIndex], idx) => {
      // Add non-highlighted text before the current range
      if (lastIndex < startIndex) {
        result.push(
          <span key={`text-${lastIndex}-${startIndex}`}>
            {text.slice(lastIndex, startIndex)}
          </span>
        );
      }

      // Add highlighted text
      result.push(
        <span
          key={`highlight-${startIndex}-${endIndex}`}
          className="highlighted-text"
        >
          {text.slice(startIndex, endIndex)}
        </span>
      );

      lastIndex = endIndex;
    });

    // Add remaining non-highlighted text
    if (lastIndex < text.length) {
      result.push(
        <span key={`text-${lastIndex}-${text.length}`}>
          {text.slice(lastIndex)}
        </span>
      );
    }

    return result;
  };

  return <p className="lineMatch" onClick={onClick} style={style}>{highlightText()}</p>;
};

export default HighlightedText;